/**
 * What changed.
 *
 * In the slot the AI journey recap used to have. The recap read well and was about a morning that
 * had already happened; this is about the app in his hand, which is the thing he has been sending
 * feedback about.
 *
 * The newest release is open, because that is the one he has not read. Older ones stay one swipe
 * away: "when did that get fixed" is a real question.
 */

import { useRef, useState } from "react";
import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp } from "lucide-react";
import { RELEASES, releaseSwipeTarget, type Release, type SwipePoint } from "../../core/whats-new";
import { BUILD_TAG } from "../build";

function dayLabel(date: string): string {
	const [year, month, day] = date.split("-").map(Number);
	if (!year || !month || !day) {
		return date;
	}
	// Midday UTC so the date cannot slide a day either way on the way to a local label.
	return new Date(Date.UTC(year, month - 1, day, 12)).toLocaleDateString("en-GB", {
		day: "numeric",
		month: "long",
		year: "numeric",
	});
}

function ReleaseBlock({ release, webBuild }: { release: Release; webBuild: string }) {
	return (
		<div className="whatsnew__release">
			<header className="whatsnew__meta">
				<h3 className="whatsnew__date">{dayLabel(release.date)}</h3>
				<span className="whatsnew__version">App {release.version} ({release.build}) · Web {webBuild}</span>
			</header>
			<ul className="whatsnew__list">
				{release.notes.map((note) => (
					<li key={note.text} className="whatsnew__note">
						<span className={`whatsnew__kind whatsnew__kind--${note.kind}`}>{note.kind === "new" ? "New" : "Fixed"}</span>
						{note.text}
					</li>
				))}
			</ul>
		</div>
	);
}

export function WhatsNew() {
	const [activeIndex, setActiveIndex] = useState(Math.max(0, RELEASES.length - 1));
	const [historyOpen, setHistoryOpen] = useState(false);
	const touchStart = useRef<SwipePoint | null>(null);
	if (RELEASES.length === 0) {
		return null;
	}
	const show = (index: number) => setActiveIndex(Math.max(0, Math.min(index, RELEASES.length - 1)));

	return (
		<section
			className="panel whatsnew"
			aria-label="What's new"
			tabIndex={0}
			onKeyDown={(event) => {
				if (historyOpen && event.key === "ArrowLeft") {
					show(activeIndex - 1);
				} else if (historyOpen && event.key === "ArrowRight") {
					show(activeIndex + 1);
				}
			}}
			onTouchStart={(event) => {
				const touch = event.touches[0];
				touchStart.current = touch ? { x: touch.clientX, y: touch.clientY } : null;
			}}
			onTouchEnd={(event) => {
				const start = touchStart.current;
				const touch = event.changedTouches[0];
				touchStart.current = null;
				if (historyOpen && start && touch) {
					show(releaseSwipeTarget(activeIndex, RELEASES.length, start, { x: touch.clientX, y: touch.clientY }));
				}
			}}
			onTouchCancel={() => {
				touchStart.current = null;
			}}
		>
			<header className="panel__head">
				<h2>What's new</h2>
			</header>
			<div className="whatsnew__viewport">
				<div className="whatsnew__pages" aria-live="polite">
					{RELEASES.map((release, index) => (
						<div
							key={release.date}
							className={`whatsnew__page ${index === activeIndex ? "is-active" : index < activeIndex ? "is-before" : "is-after"}`}
							aria-hidden={index !== activeIndex}
						>
							<ReleaseBlock release={release} webBuild={release.webBuild ?? BUILD_TAG} />
						</div>
					))}
				</div>
			</div>
			{RELEASES.length > 1 ? (
				<button
					type="button"
					className="whatsnew__history-toggle"
					aria-expanded={historyOpen}
					onClick={() => {
						setHistoryOpen((open) => {
							if (open) setActiveIndex(RELEASES.length - 1);
							return !open;
						});
					}}
				>
					{historyOpen ? <ChevronUp aria-hidden="true" size={16} /> : <ChevronDown aria-hidden="true" size={16} />}
					{historyOpen ? "Hide release history" : `Show ${RELEASES.length - 1} earlier releases`}
				</button>
			) : null}
			{historyOpen && RELEASES.length > 1 ? (
				<nav className="whatsnew__controls" aria-label="Release history">
					<button
						type="button"
						className="whatsnew__navbtn"
						aria-label="Older release"
						title="Older release"
						disabled={activeIndex === 0}
						onClick={() => show(activeIndex - 1)}
					>
						<ChevronLeft aria-hidden="true" size={18} strokeWidth={2} />
					</button>
					<div className="whatsnew__dots" aria-label={`Release ${activeIndex + 1} of ${RELEASES.length}`}>
						{RELEASES.map((release, index) => (
							<button
								type="button"
								key={release.date}
								className={`whatsnew__dot ${index === activeIndex ? "is-active" : ""}`}
								aria-label={`Show ${dayLabel(release.date)} release`}
								aria-current={index === activeIndex ? "true" : undefined}
								onClick={() => show(index)}
							/>
						))}
					</div>
					<button
						type="button"
						className="whatsnew__navbtn"
						aria-label="Newer release"
						title="Newer release"
						disabled={activeIndex === RELEASES.length - 1}
						onClick={() => show(activeIndex + 1)}
					>
						<ChevronRight aria-hidden="true" size={18} strokeWidth={2} />
					</button>
				</nav>
			) : null}
		</section>
	);
}
